/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faCalendarAlt,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { toast } from 'react-toastify';
import LoadingModal from '../../components/LoadingModal';
import HttpService from '../../services/Http';
import ApiErrorHandler from '../../utils/ApiErrorHandler';
import './FarmerDeregistrationRequests.scss';

const DEFAULT_FILTERS = {
  role: '',
  fromDate: '',
  toDate: '',
};

const FarmerDeregistrationRequests = () => {
  const role = localStorage.getItem('role');
  const email = (localStorage.getItem('email') || '').toLowerCase();
  const canAccessDeregistrationRequests = role === 'admin' && email === 'admin@heritagevetplus.com';
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 10;

  const totalPages = Math.ceil(totalCount / limit) || 1;

  const fetchRequests = async (pageNumber = 1, values = appliedFilters) => {
    try {
      setIsLoading(true);
      const response = await HttpService.get('/admin/farmer-deregistration-requests', {
        page: pageNumber,
        limit,
        ...values,
      });
      const data = response?.data || {};

      setRequests(data.rows || []);
      setTotalCount(data.count || 0);
      setPage(pageNumber);
    } catch (error) {
      toast.error(ApiErrorHandler.selectMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await HttpService.get('/admin/farmer-deregistration-filter-options');
      setRoleOptions(response?.data?.roles || []);
    } catch (error) {
      toast.error(ApiErrorHandler.selectMessage(error));
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchRequests(1, appliedFilters);
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    fetchRequests(1, filters);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    fetchRequests(1, DEFAULT_FILTERS);
  };

  const previousPage = () => {
    if (page > 1) {
      fetchRequests(page - 1);
    }
  };

  const nextPage = () => {
    if (page < totalPages) {
      fetchRequests(page + 1);
    }
  };

  if (!canAccessDeregistrationRequests) {
    return <Redirect to="/admin/users/pending" />;
  }

  return (
    <>
      <div className="farmer-deregistration-requests">
        <div className="row">
          <div className="col-12">
            <div className="page-header">
              <div>
                <p className="page-header__title">
                  Farmer De Registration Requests
                </p>
                <p className="page-header__subtitle">
                  {totalCount}
                  {' '}
                  requests found
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="filter-card">
          <div className="filter-card__header">
            <FontAwesomeIcon icon={faFilter} className="filter-card__icon" />
            <span>Filters</span>
          </div>
          <div className="filter-card__grid">
            <div>
              <label htmlFor="role" className="filter-card__label">
                Role
                <select
                  id="role"
                  name="role"
                  className="filter-card__field"
                  value={filters.role}
                  onChange={handleFilterChange}
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((item) => (
                    <option value={item} key={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label htmlFor="fromDate" className="filter-card__label">
                From Date
                <input
                  id="fromDate"
                  name="fromDate"
                  type="date"
                  className="filter-card__field"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                />
              </label>
            </div>
            <div>
              <label htmlFor="toDate" className="filter-card__label">
                To Date
                <input
                  id="toDate"
                  name="toDate"
                  type="date"
                  className="filter-card__field"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                />
              </label>
            </div>
            <div>
              <div className="filter-card__actions">
                <button type="button" className="filter-card__button filter-card__button--primary" onClick={applyFilters}>
                  Filter
                </button>
                <button type="button" className="filter-card__button" onClick={clearFilters}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="table-container">
          <div className="table-scroll">
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Farmer Id</th>
                  <th>Farmer Name</th>
                  <th>Phone</th>
                  <th>Requested</th>
                  <th>Plant</th>
                  <th>Region</th>
                  <th>Aadhar</th>
                  <th>Role</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((item) => (
                  <tr key={item.id}>
                    <td className="requests-table__id">{item.farmerId || '-'}</td>
                    <td className="requests-table__name">{item.farmerName || '-'}</td>
                    <td>{item.phone || '-'}</td>
                    <td>{item.requestedAt ? moment(item.requestedAt).format('DD MMM YYYY, hh:mm a') : '-'}</td>
                    <td>{item.plantCode || '-'}</td>
                    <td>{item.regionName || '-'}</td>
                    <td>{item.adharNumber || '-'}</td>
                    <td>
                      <span className="requests-table__role">{item.role || '-'}</span>
                    </td>
                    <td className="requests-table__reason">{item.reason || '-'}</td>
                    <td>
                      <span className={`requests-table__status requests-table__status--${item.status || 'pending'}`}>
                        {item.status || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && requests.length === 0 ? (
            <div className="notFound">
              <FontAwesomeIcon icon={faCalendarAlt} className="notFound__icon" />
              <span className="notFound__text">No de registration requests found</span>
            </div>
          ) : null}
        </div>

        <div className="pagination-bar">
          <span className="pagination-bar__summary">
            Page
            {' '}
            {page}
            {' '}
            of
            {' '}
            {totalPages}
          </span>
          <div className="pagination">
            <button
              type="button"
              className="pagination__btn"
              disabled={page <= 1}
              onClick={previousPage}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} className="pagination__btn__icon" />
            </button>
            <span className="pagination__page">
              {page}
              {' '}
              /
              {' '}
              {totalPages}
            </span>
            <button
              type="button"
              className="pagination__btn"
              disabled={page >= totalPages}
              onClick={nextPage}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} className="pagination__btn__icon" />
            </button>
          </div>
        </div>
      </div>
      <LoadingModal show={isLoading} />
    </>
  );
};

export default FarmerDeregistrationRequests;
